import { useQuery } from '@apollo/client';
import { assertNotNil } from '../../utils/types';
import { GET_GEO_CONFIG } from '../../utils/apollo/globalConfig';
import pako from 'pako';
import { useAppDispatch } from '../../redux/hooks';
import {
  ICountyMapProperties,
  IStateMapProperties,
  ITopoJsonData,
  setGeoMapData,
} from '../../redux/slices/bootstrap';
import { Arc, GeometryObject } from 'topojson-specification';
import { useShowGeo } from '../../utils/hooks';
import {
  IGeoConfigQuery,
  IGeoConfigQueryVariables,
} from '../../utils/apollo/graphqlSchema';

interface IRawData {
  dma_map: IRawTopoJsonMapData<ICountyMapProperties>;
  state_map: IRawTopoJsonMapData<IStateMapProperties>;
}

interface IRawTopoJsonMapData<
  T extends ICountyMapProperties | IStateMapProperties,
> {
  type: 'Topology';
  arcs: Arc[];
  objects: {
    [K in T extends ICountyMapProperties ? 'us-county-map' : 'us-state-map']: {
      geometries: GeometryObject<T>[];
    };
  };
}

const reconstructTopoJsonData = (
  data: IRawData,
): {
  stateMapData: ITopoJsonData<IStateMapProperties>;
  countyMapData: ITopoJsonData<ICountyMapProperties>;
} => {
  // need to work with Kobol to update the name, not urgent
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { state_map, dma_map } = data;

  return {
    stateMapData: {
      ...state_map,
      objects: {
        'us-state-map': {
          ...state_map.objects['us-state-map'],
          type: 'GeometryCollection' as const,
        },
      },
    },
    countyMapData: {
      ...dma_map,
      objects: {
        'us-county-map': {
          ...dma_map.objects['us-county-map'],
          type: 'GeometryCollection' as const,
        },
      },
    },
  };
};

const LoadGeoMap = () => {
  const dispatch = useAppDispatch();
  const geoMapEnabled = useShowGeo();

  useQuery<IGeoConfigQuery, IGeoConfigQueryVariables>(GET_GEO_CONFIG, {
    onCompleted: ({ geoConfig }) => {
      const url = geoConfig.mapFileUrl;
      assertNotNil(url);
      // fetch gzip file from url
      fetch(url)
        .then((response) => {
          if (!response.ok) {
            throw new Error('Geo Data Fetch failed');
          }
          return response.arrayBuffer();
        })
        .then((arrayBuffer) => {
          // Use pako to decompress the data
          return pako.inflate(new Uint8Array(arrayBuffer), {
            to: 'string',
          });
        })
        .then((content) => {
          if (content) {
            // Parse the JSON content
            const mapData = JSON.parse(content);
            const { stateMapData, countyMapData } =
              reconstructTopoJsonData(mapData);
            dispatch(
              setGeoMapData({
                stateMapData,
                countyMapData,
              }),
            );
          }
        })
        .catch((error) => {
          console.error('Error saving geo map data:', error);
        });
    },
    skip: !geoMapEnabled,
  });

  return <></>;
};

export default LoadGeoMap;
